import { Twitter, MessageCircle, Instagram, Heart, CreditCard } from 'lucide-react';
import { languageService } from '../../js/languageService';

export default function Footer() {
  return (
    <footer className="relative mt-10 pb-8 md:pb-16 overflow-hidden">
      {/* Decorative gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-950/2 to-transparent pointer-events-none"></div>
        <div className="absolute -bottom-20 right-0 w-80 h-80 bg-purple-900/5 rounded-full blur-[100px]"></div>
        {/* New gradient effect */}
        <div className="absolute bottom-0 left-0 w-full h-[250px] bg-gradient-to-t from-theme-background/95 to-transparent"></div>
        <div className="absolute -bottom-40 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px]"></div>
        <div className="absolute -bottom-40 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[100px]"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6">
        <div className="glass-card p-6 md:p-10 lg:p-12 border border-blue-500/10 hover-glow relative overflow-hidden rounded-2xl">
          {/* Shine effect */}
          <div className="absolute -inset-full h-full w-1/2 z-5 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 transform -translate-x-full animate-[shine_8s_infinite]"></div>
        
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Logo & About */}
            <div className="sm:col-span-2 lg:col-span-5">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <img 
                  src="/quokka.png" 
                  alt="QuokkaX Logo" 
                  className="w-10 h-10 md:w-12 md:h-12" 
                />
                <h3 className="text-xl md:text-2xl font-bold gradient-heading" data-i18n="footer.title">QuokkaX</h3>
              </div>
              
              <p className="text-sm md:text-base card-text mb-4 md:mb-6" data-i18n="footer.description">
                A revolutionary memecoin token with purpose. Participate in the presale and join our growing community.
              </p>
              
              {/* Direct CTA Button */}
              <button className="w-full sm:w-auto gradient-button py-2.5 px-5 mb-6 text-white font-medium text-sm rounded-full flex items-center justify-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span data-i18n="footer.buy_now">Buy Now</span>
              </button>
              
              <div className="flex items-center text-xs md:text-sm card-text">
                <Heart className="w-4 h-4 mr-2 text-primary" />
                <span data-i18n="footer.made_with_love">Made with love by the QuokkaX team</span>
              </div>
            </div>
            
            {/* Quick Links */}
            <div className="sm:col-span-1 lg:col-span-3 lg:col-start-7">
              <h4 className="text-base md:text-lg font-bold mb-4 md:mb-5 gradient-heading" data-i18n="footer.navigation">Navigation</h4>
              <nav>
                <ul className="space-y-2 md:space-y-3">
                  <li>
                    <a href="#home" className="text-sm md:text-base card-text hover:text-primary transition-colors flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      <span data-i18n="footer.home">Home</span>
                    </a>
                  </li>
                  <li>
                    <a href="#tokenomics" className="text-sm md:text-base card-text hover:text-primary transition-colors flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      <span data-i18n="footer.tokenomics">Tokenomics</span>
                    </a>
                  </li>
                  <li>
                    <a href="#presale" className="text-sm md:text-base card-text hover:text-primary transition-colors flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      <span data-i18n="footer.presale">Presale</span>
                    </a>
                  </li>
                  <li>
                    <a href="#roadmap" className="text-sm md:text-base card-text hover:text-primary transition-colors flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      <span data-i18n="footer.roadmap">Roadmap</span>
                    </a>
                  </li>
                  <li>
                    <a href="#faq" className="text-sm md:text-base card-text hover:text-primary transition-colors flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      <span data-i18n="footer.faq">FAQ</span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
            
            {/* Community */}
            <div className="sm:col-span-2 lg:col-span-4 lg:col-start-10">
              <h4 className="text-base md:text-lg font-bold mb-4 md:mb-5 gradient-heading text-center" data-i18n="footer.community">Community</h4>
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                <a href="https://twitter.com/quokkax" aria-label="Twitter" className="glass-card w-10 h-10 rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors">
                  <Twitter size={18} />
                </a>
                <a href="https://t.me/quokkax" aria-label="Telegram" className="glass-card w-10 h-10 rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors">
                  <MessageCircle size={18} />
                </a>
                <a href="https://instagram.com/quokkax" aria-label="Instagram" className="glass-card w-10 h-10 rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors">
                  <Instagram size={18} />
                </a>
              </div>
              
              <div className="text-sm card-text text-center">
                <p className="mb-2" data-i18n="footer.have_questions">Have questions?</p>
                <a href="mailto:hello@quokkax.com" className="text-primary hover:underline">
                  hello@quokkax.com
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom footer */}
        <div className="mt-6 md:mt-10 flex flex-col md:flex-row justify-between items-center py-4 border-t border-gray-800/30">
          <div className="text-xs md:text-sm card-text">
            &copy; {new Date().getFullYear()} <span data-i18n="footer.copyright">QuokkaX. All rights reserved.</span>
          </div>
          
          <div className="flex gap-4 md:gap-8 text-xs md:text-sm">
            <a href="/terms" className="card-text hover:text-primary transition-colors" data-i18n="footer.terms">
              Terms of Service
            </a>
            <a href="/privacy" className="card-text hover:text-primary transition-colors" data-i18n="footer.privacy">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
} 